import React from 'react';
import Icon, { ShopOutlined } from '@ant-design/icons';
import type { RouteConfig } from 'types';
import { hidePayment } from 'services/chattt';
import { activityTypeKeyMap } from './NewuserIncentive';
import { incentiveTypes, shouldShowIncentive } from './NewuserIncentive/state';
import { isDemoChannel } from 'services/channels/demo';
import { ReactComponent as iconService } from 'views/Service/Market/assets/nav.svg';
import { ReactComponent as iconFeedback } from 'views/Main/components/AppSider/assets/feedback.svg';
import { ReactComponent as iconItem } from './assets/item.svg';
import { ReactComponent as iconStaff } from './assets/staff.svg';
import { ReactComponent as iconBuyer } from './assets/buyer.svg';
import { getPermission } from 'hooks/business/useNewPermisson';

import {
  isAeProduct,
  isChatppProduct,
  isMasterOnline,
  isSiteGiant,
  isTfsoftware,
} from 'productLine';
import { quickChatRouterControl } from 'components/RouterRender';
import TFMarket from 'views/Service/Market/TFMarket';

import Shops from 'views/Setting/Shops';
import Account from 'views/Assistant/Account';
import Group from 'views/Assistant/Group';
import Department from 'views/Assistant/Department';

import BlockList from 'views/Setting/BlockList';
import Enterprise from 'views/Setting/Enterprise';

import AppCenter from 'views/AppCenter';
import Market from 'views/Service/Market';
import ERP from './ERP';

import WhatsappBuyers from './WhatsappBuyers';
import WhatsappWallet from './WhatsappWallet';
import NewuserIncentive from './NewuserIncentive';
import AeOverview from './AeOverview';
import Feedback from './Feedback';
import TFERP from './TFERP';
import SensitiveWord from 'views/Setting/SensitiveWord';
import ItemGroupEntry from './ItemManage/ItemGroup/Index';
import EasyRefundBuyer from './EasyRefundBuyer/index';

type Route = Util.PartialRequired<RouteConfig, 'path'> & {
  icon?: React.ReactNode;
};

const shopsChildRoutes: Route[] = [
  {
    path: 'store',
    redirect: true,
    i18nHelmetTitle: 'scene.managementCenter.navigation.store.authorization',
    element: <Shops />,
    access: ({ newPermissionEnable, newPermissionMap }) => {
      return isAeProduct ||
        getPermission({
          newPermissionEnable,
          newPermissionMap,
          permissionName: '店铺管理',
        })
        ? 'write'
        : false;
    },
  },
  {
    path: 'group',
    element: <Group />,
    i18nHelmetTitle: 'nav.group',
    access: ({ newPermissionEnable, permissionMap: { ASSISTANT_MANAGER } }) =>
      newPermissionEnable === false && ASSISTANT_MANAGER ? 'write' : false,
  },
  {
    path: 'sensitive-word',
    element: <SensitiveWord />,
    i18nHelmetTitle: 'scene.managementCenter.navigation.store.restrictedWords',
    access: ({ newPermissionEnable, newPermissionMap }) => {
      return getPermission({
        newPermissionEnable,
        newPermissionMap,
        permissionName: '敏感词',
      })
        ? 'write'
        : false;
    },
  },
  {
    path: 'erp',
    i18nHelmetTitle: 'scene.managementCenter.navigation.store.erp',
    element: <ERP />,
    access: ({
      newPermissionEnable,
      newPermissionMap,
      orgConfig: { whiteListConfig: { ERP = false } = {} },
    }) => {
      return ERP &&
        getPermission({
          newPermissionEnable,
          newPermissionMap,
          permissionName: '店铺管理',
        })
        ? 'write'
        : false;
    },
  },
];

export const shopsRoute: Route = {
  path: 'shops',
  redirect: true,
  access: (props) => {
    const {
      newPermissionEnable,
      permissionMap: { SHOP_MANAGER },
    } = props;

    if (newPermissionEnable === undefined) {
      return false;
    }

    if (newPermissionEnable === false) {
      return SHOP_MANAGER ? 'write' : false;
    }

    return shopsChildRoutes.some(({ access }) => !access || access(props)) ? 'write' : false;
  },
  i18nHelmetTitle: 'scene.managementCenter.navigation.store',
  icon: <ShopOutlined />,
  children: shopsChildRoutes,
};

const staffChildRoutes: Route[] = [
  {
    path: 'account',
    element: <Account />,
    i18nHelmetTitle: 'scene.managementCenter.navigation.staff.account',
    access: ({
      newPermissionEnable,
      newPermissionMap,
      permissionMap: { ASSISTANT_MANAGER },
      orgConfig: { whiteListConfig: { AE_CONVERSATION = false } = {} },
    }) => {
      return (!isAeProduct || AE_CONVERSATION) &&
        getPermission({
          newPermissionEnable,
          newPermissionMap,
          permissionName: '帐号管理',
          fallbackPermission: ASSISTANT_MANAGER === true,
        })
        ? 'write'
        : false;
    },
  },
  {
    path: 'department',
    element: <Department />,
    i18nHelmetTitle: 'scene.managementCenter.navigation.staff.department',
    access: ({
      newPermissionEnable,
      newPermissionMap,
      orgConfig: { whiteListConfig: { AE_CONVERSATION = false } = {} },
    }) => {
      return (!isAeProduct || AE_CONVERSATION) &&
        getPermission({
          newPermissionEnable,
          newPermissionMap,
          permissionName: '帐号管理',
          fallbackPermission: false,
        })
        ? 'write'
        : false;
    },
  },
  {
    path: 'tferp',
    element: <TFERP />,
    i18nHelmetTitle: 'scene.managementCenter.navigation.staff.tfErp',
    access: ({ newPermissionEnable, newPermissionMap, orgConfig: { whiteListConfig } }) => {
      return (isTfsoftware || whiteListConfig?.TF_ERP_ENABLE) &&
        getPermission({
          newPermissionEnable,
          newPermissionMap,
          permissionName: '帐号管理',
        })
        ? 'write'
        : false;
    },
  },
];

export const staffRoute: Route = {
  path: 'staff',
  redirect: true,
  access: (props) => {
    const { newPermissionEnable } = props;

    if (newPermissionEnable === undefined) {
      return false;
    }

    return staffChildRoutes.some(({ access }) => !access || access(props)) ? 'write' : false;
  },
  i18nHelmetTitle: 'scene.managementCenter.navigation.staff',
  icon: <Icon component={iconStaff} />,
  children: staffChildRoutes,
};

const blockListChildRoutes: Route[] = [
  {
    path: 'blockList',
    redirect: true,
    i18nHelmetTitle: 'scene.managementCenter.navigation.buyer.buyerBlocklist',
    element: <BlockList />,
    access: ({ newPermissionEnable, newPermissionMap, permissionMap: { SUPERVISE } }) =>
      getPermission({
        newPermissionEnable,
        newPermissionMap,
        permissionName: '买家管理',
        fallbackPermission: SUPERVISE === true,
      })
        ? 'write'
        : false,
  },
  {
    path: 'easyRefundBuyer',
    redirect: true,
    i18nHelmetTitle: 'scene.managementCenter.navigation.buyer.highRefundRiskBuyers',
    element: <EasyRefundBuyer />,
    access: ({
      newPermissionEnable,
      newPermissionMap,
      permissionMap: { SUPERVISE },
      orgConfig: { whiteListConfig },
    }) => {
      const perm = getPermission({
        newPermissionEnable,
        newPermissionMap,
        permissionName: '买家管理',
        fallbackPermission: SUPERVISE === true,
      });
      return whiteListConfig?.SHOPEE_ZDPF && perm ? 'write' : false;
    },
  },
  {
    path: 'waBuyerList',
    redirect: true,
    i18nHelmetTitle: 'scene.managementCenter.navigation.buyer.waBuyer',
    element: <WhatsappBuyers />,
    access: ({ newPermissionEnable, newPermissionMap, permissionMap: { SUPERVISE } }) =>
      getPermission({
        newPermissionEnable,
        newPermissionMap,
        permissionName: '买家管理',
        fallbackPermission: SUPERVISE === true,
      })
        ? 'write'
        : false,
  },
];

export const blockListRoute: Route = {
  path: 'buyer',
  redirect: true,
  i18nHelmetTitle: 'scene.managementCenter.navigation.buyer',
  access: (props) =>
    !isAeProduct && blockListChildRoutes.some(({ access }) => !access || access(props))
      ? 'write'
      : false,
  icon: <Icon component={iconBuyer} />,
  children: blockListChildRoutes,
};

export const enterpriseRoute: Util.PartialRequired<RouteConfig, 'i18nHelmetTitle'> = {
  path: 'enterprise',
  redirect: true,
  i18nHelmetTitle: 'scene.managementCenter.navigation.seller.profile',
  access: quickChatRouterControl(({ permissionMap: { SHOP_MANAGER } }) =>
    SHOP_MANAGER ? 'write' : false,
  ),
  element: <Enterprise />,
};

export const serviceAccess: Required<Route>['access'] = ({ permissionMap: { SUPERVISE } }) =>
  SUPERVISE && !isAeProduct ? 'write' : false;

export const serviceRoute: Route = {
  path: 'service',
  redirect: true,
  i18nHelmetTitle: 'scene.managementCenter.navigation.seller',
  access: serviceAccess,
  icon: <Icon component={iconService} />,
  children: [
    {
      path: 'market',
      i18nHelmetTitle: 'scene.managementCenter.navigation.seller.planAndBilling',
      element: isTfsoftware ? <TFMarket /> : <Market />,
      access: () => (hidePayment || isMasterOnline || isSiteGiant ? false : 'write'),
    },
    {
      path: 'waWallet',
      i18nHelmetTitle: 'nav.waWallet',
      element: <WhatsappWallet />,
      access: () => (isSiteGiant || isMasterOnline ? false : 'write'),
    },
    enterpriseRoute,
    {
      path: 'appCenter',
      i18nHelmetTitle: 'scene.managementCenter.navigation.seller.partners',
      element: <AppCenter />,
      access: quickChatRouterControl(() =>
        isDemoChannel || isMasterOnline || isSiteGiant ? false : 'write',
      ),
    },
    ...incentiveTypes.map<RouteConfig>((activityType) => {
      return {
        path: 'newuserIncentive',
        i18nHelmetTitle: activityTypeKeyMap[activityType],
        element: <NewuserIncentive />,
        access: quickChatRouterControl(({ incentiveType }) =>
          shouldShowIncentive({ incentiveType, activityType, isSuperviser: true }) &&
          !isMasterOnline &&
          !isSiteGiant
            ? 'write'
            : false,
        ),
      };
    }),
  ],
};

export const aeServiceRoute: Route = {
  path: 'service',
  redirect: true,
  i18nHelmetTitle: 'scene.managementCenter.navigation.seller',
  icon: <Icon component={iconService} />,
  access: () => (isAeProduct ? 'write' : false),
  children: [
    {
      path: 'aeoverview',
      i18nHelmetTitle: 'aeoverview.服务概况',
      element: <AeOverview />,
    },
  ],
};

export const feedbackRoute: Route = {
  path: 'feedback',
  redirect: true,
  i18nHelmetTitle: 'scene.managementCenter.navigation.caseReported',
  icon: <Icon component={iconFeedback} />,
  element: <Feedback />,
  access: quickChatRouterControl(
    ({ orgConfig: { whiteListConfig: { AE_CONVERSATION = false } = {} } }) =>
      isChatppProduct || AE_CONVERSATION ? 'write' : false,
  ),
};

export const itemRoute: Route = {
  path: 'item',
  redirect: true,
  i18nHelmetTitle: 'scene.managementCenter.navigation.product',
  icon: <Icon component={iconItem} />,
  access: ({
    newPermissionEnable,
    newPermissionMap,
    permissionMap: { SUPERVISE },
    orgConfig: { whiteListConfig: { USE_ITEM_GROUP = false } = {} },
  }) => {
    if (!USE_ITEM_GROUP) return false;
    const access = getPermission({
      newPermissionEnable,
      newPermissionMap,
      permissionName: '商品组',
      fallbackPermission: SUPERVISE === true,
    });
    return access ? 'write' : false;
  },
  children: [
    {
      path: 'group',
      i18nHelmetTitle: 'scene.managementCenter.navigation.product.productGroup',
      element: <ItemGroupEntry />,
    },
  ],
};

export const childRoutes: Route[] = [
  shopsRoute,
  staffRoute,
  blockListRoute,
  itemRoute,
  serviceRoute,
  aeServiceRoute,
  feedbackRoute,
];
